import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { useCreateTenant } from "@/hooks/useCreateTenant";
import { toast } from "@/utils/toast";
import { Button } from "@/components/button";
import { Input } from "@/components/ui/input";
import { cn } from "utils/cn";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

function buildSchema(t: (key: string) => string) {
  return yup
    .object({
      name: yup
        .string()
        .trim()
        .required(t("admin:tenants.create.nameRequired")),
    })
    .required();
}

type FormValues = { name: string };

interface CreateTenantModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateTenantModal({
  open,
  onOpenChange,
}: CreateTenantModalProps) {
  const { t } = useTranslation(["admin"]);
  const { createTenant, isCreating } = useCreateTenant();

  const resolver = useMemo(() => yupResolver(buildSchema(t)), [t]);

  const { register, handleSubmit, formState, reset } = useForm<FormValues>({
    resolver,
    defaultValues: { name: "" },
    mode: "onSubmit",
  });

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit = handleSubmit(async ({ name }) => {
    const success = await createTenant(name);
    if (success) {
      toast.success(t("admin:tenants.create.successToast"));
      onOpenChange(false);
    } else {
      toast.error(t("admin:tenants.create.errorToast"));
    }
  });

  const isSubmitDisabled = isCreating || formState.isSubmitting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-auto min-w-[420px] px-4 py-4"
        data-testid="create-tenant-modal"
      >
        <DialogHeader>
          <DialogTitle>{t("admin:tenants.create.title")}</DialogTitle>
          <DialogDescription>
            {t("admin:tenants.create.description")}
          </DialogDescription>
        </DialogHeader>

        <form className="grid gap-4" onSubmit={onSubmit} noValidate>
          <div className="space-y-1.5">
            <label
              htmlFor="create-tenant-name"
              className="text-sm font-medium text-secondary-700"
            >
              {t("admin:tenants.create.nameLabel")}
            </label>
            <Input
              id="create-tenant-name"
              testId="create-tenant-name-input"
              autoFocus
              placeholder={t("admin:tenants.create.namePlaceholder")}
              className={cn(
                formState.errors.name?.message &&
                  "border border-system-error ring-2 ring-destructive shadow-form focus:shadow-form",
              )}
              aria-invalid={Boolean(formState.errors.name)}
              {...register("name")}
            />
            <div
              className={cn(
                "text-xs font-normal italic pl-[0.625rem] min-h-[1rem]",
                formState.errors.name && "text-destructive",
              )}
            >
              {formState.errors.name?.message ?? ""}
            </div>
          </div>

          <DialogFooter className="sm:justify-end">
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                label={t("admin:tenants.create.cancel")}
                disabled={isSubmitDisabled}
              />
            </DialogClose>
            <Button
              type="submit"
              variant="default"
              label={t("admin:tenants.create.submit")}
              loading={isCreating}
              disabled={isSubmitDisabled}
              testId="create-tenant-submit"
            />
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
